import { useIsMobile } from '@epilot/concorde-elements'
import type {
  EnumImageButtonProps,
  EnumImageOption
} from '@epilot/journey-logic-commons'
import { appendStepBlockId } from '@epilot/journey-logic-commons'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { ChoiceButton } from '../components/Choice/ChoiceButton'
import classes from '../components/Choice/ChoiceButton.module.scss'
import { ChoiceImage } from '../components/Choice/ChoiceImage'
import imgClasses from '../components/Choice/ChoiceImage.module.scss'
import { ChoiceWrapper } from '../components/Choice/ChoiceWrapper'
import { IconLabel } from '../components/Choice/IconLabel'

export const EnumImageControl = (
  props: EnumImageButtonProps & {
    IMAGE_PREVIEW_API_URL: string
    isAnimationEffectsEnabled: boolean
  }
) => {
  const {
    handleChange,
    path,
    data,
    options,
    optionsImageDescriptions,
    hasError,
    label,
    isRequired,
    imageType,
    defaultSelect,
    id,
    IMAGE_PREVIEW_API_URL,
    isAnimationEffectsEnabled
  } = props

  const { t } = useTranslation()

  const handleOnChange = (value: string | undefined) => {
    const newValue = !value && defaultSelect ? defaultSelect : value

    handleChange(path, newValue)
  }

  const isMobile = useIsMobile()

  const buttons = options.map((option: EnumImageOption, index: number) => {
    const label = option.label
    const value = option.value
    const imageId = option.image?.id
    const url = option.image?.url
    const imageDescription = optionsImageDescriptions?.[index]

    const fieldId = appendStepBlockId(id, label)
    const isSelected = data === value

    return (
      <ChoiceButton
        aria-describedby={hasError ? `${id}-errorMessage` : undefined}
        aria-label={imageDescription ? `${label}, ${imageDescription}` : label}
        aria-selected={isSelected}
        className={classNames(
          classes.imageButton,
          isSelected && 'Concorde-SingleChoice-Image__Selected'
        )}
        id={fieldId}
        isSelected={isSelected}
        key={value}
        label={<IconLabel iconConfig={option.icon} label={label} />}
        onClick={() => handleOnChange(value === data ? undefined : value)}
        role="option"
      >
        <ChoiceImage
          IMAGE_PREVIEW_API_URL={IMAGE_PREVIEW_API_URL}
          alt={imageDescription}
          imageId={imageId}
          imageType={imageType}
          isAnimated={isAnimationEffectsEnabled}
          url={url}
        />
      </ChoiceButton>
    )
  })

  return (
    <ChoiceWrapper
      className="Concorde-SingleChoice-Image-Block"
      hasError={hasError}
      id={id}
      isRequired={isRequired}
      label={label}
    >
      <div
        aria-label={label || t('Please select one of the options')}
        className={classNames(
          classes.buttonWrapRow,
          isMobile &&
            classNames(classes.buttonWrapColumn, imgClasses.imageButtonWidth)
        )}
        role="listbox"
      >
        {buttons}
      </div>
    </ChoiceWrapper>
  )
}
